import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { combineLatest, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap, delay } from 'rxjs/operators';
import { CommonService } from 'src/app/share/services/common.service';
import { ShellService } from 'src/app/shell/shell.service';

@Component({
  selector: 'app-credit-card-identity-capture',
  templateUrl: './credit-card-identity-capture.component.html',
  styleUrls: ['./credit-card-identity-capture.component.scss']
})
export class CreditCardIdentityCaptureComponent implements OnInit {

  vm$: Observable<IViewModel>;

  private _window: Window;

  @Input() token: string;

  /**
   * 
   * @param shell 
   */
  constructor(private shell: ShellService, private apiSvc: CommonService) {
    this._window = window;

    this.shell.showChrome = false;
  }

  @ViewChild('video', { static: false }) videoElement: ElementRef;
  @ViewChild('canvas', { static: false }) canvasElement: ElementRef;

  private log(context: any) {
    console.log(context);
  }

  //#region drawing

  private ongoingTouches = [];

  private colorForTouch(touch) {
    const r = touch.identifier % 16;
    const g = Math.floor(touch.identifier / 3) % 16;
    const b = Math.floor(touch.identifier / 7) % 16;

    //const color = `#${r.toString(16)}${g.toString(16)}${b.toString(16)}`;
    //const color = '#FF0000'; /* red */
    const color = '#000000'; /* red */
    return color;
  }

  private copyTouch(source: { identifier, pageX, pageY, clientX, clientY }) {
    const offset = {
      left: this.canvasElement.nativeElement.offsetLeft,
      top: this.canvasElement.nativeElement.offsetTop
    };
    const rect = this.canvasElement.nativeElement.getBoundingClientRect();
    this.log({
      rect: rect,
      source: source,
      offset: offset
    });

    return {
      identifier: source.identifier,
      x: source.clientX - rect.x,
      y: source.clientY - rect.y
    };
  }


  private ongoingTouchIndexById(idToFind) {
    for (let i = 0; i < this.ongoingTouches.length; i++) {
      const id = this.ongoingTouches[i].identifier;

      if (id == idToFind) {
        return i;
      }
    }
    return -1;    // not found
  }

  /**
   * 
   * @param vm 
   * @param evt 
   * @returns 
   */
  drawStart(vm: IViewModel, evt: any) {
    try {
      vm.errorText = undefined;

      evt.preventDefault();
      this.log('touchstart.');

      const el = this.canvasElement.nativeElement;

      const ctx = el.getContext('2d');
      const touches = evt.changedTouches;
      if (!touches)
        return;

      for (let i = 0; i < touches.length; i++) {
        this.log(`touchstart: ${i}.`);

        const touch = this.copyTouch(touches[i]);
        this.ongoingTouches.push(touch);

        const color = this.colorForTouch(touch);
        this.log(`color of touch with id ${touch.identifier} = ${color}`);

        ctx.beginPath();

        ctx.arc(touch.x, touch.y, 4, 0, 2 * Math.PI, false);  // a circle at the start
        ctx.fillStyle = color;
        ctx.fill();
        this.log(`ctx.arc.. ${touch.x}, ${touch.y} );`);
      }

    } catch (err) {
      vm.errorText = err;
    }

  }

  /**
   * 
   * @param vm 
   * @param evt 
   * @returns 
   */
  drawEnd(vm: IViewModel, evt: any) {

    try {
      vm.errorText = undefined;

      evt.preventDefault();
      this.log("touchend");
      const el = this.canvasElement.nativeElement;
      const ctx = el.getContext('2d');

      const touches = evt.changedTouches;
      if (!touches)
        return;


      for (let i = 0; i < touches.length; i++) {
        const touch = this.copyTouch(touches[i]);

        const color = this.colorForTouch(touch);
        let idx = this.ongoingTouchIndexById(touch.identifier);

        if (idx >= 0) {
          ctx.lineWidth = 4;
          ctx.fillStyle = color;

          ctx.beginPath();

          const moveToPoint = this.ongoingTouches[idx];
          ctx.moveTo(moveToPoint.x, moveToPoint.y);

          this.log(`ctx.lineTo( ${touch.x}, ${touch.y} );`);
          ctx.lineTo(touch.x, touch.y);

          ctx.fillRect(touch.x - 4, touch.y - 4, 8, 8);  // and a square at the end
          this.ongoingTouches.splice(idx, 1);  // remove it; we're done
        } else {
          this.log('can\'t figure out which touch to end');
        }
      }

      // enable clear once drawn
      const a = vm.actions.find(a => a.code == 'clear');
      if (a)
        a.enabled = true;

    } catch (err) {
      vm.errorText = err;
    }
  }

  /**
   * 
   * @param vm 
   * @param evt 
   * @returns 
   */
  drawMove(vm: IViewModel, evt: any) {
    try {
      vm.errorText = undefined;

      evt.preventDefault();
      const el = this.canvasElement.nativeElement;
      const ctx = el.getContext('2d');

      const touches = evt.changedTouches;
      if (!touches)
        return;

      for (let i = 0; i < touches.length; i++) {
        const touch = this.copyTouch(touches[i]);

        const color = this.colorForTouch(touch);
        const idx = this.ongoingTouchIndexById(touch.identifier);

        if (idx >= 0) {
          this.log(`continuing touch ${idx}`);
          ctx.beginPath();

          const moveToPoint = this.ongoingTouches[idx];
          this.log(`ctx.moveTo( ${moveToPoint.x}, ${moveToPoint.y} );`);
          ctx.moveTo(moveToPoint.x, moveToPoint.y);

          this.log(`ctx.lineTo( ${touch.x}, ${touch.y} );`);
          ctx.lineTo(touch.x, touch.y);

          ctx.lineWidth = 4;
          ctx.strokeStyle = color;
          ctx.stroke();

          this.ongoingTouches.splice(idx, 1, touch);  // swap in the new touch record
        } else {
          this.log('can\'t figure out which touch to continue');
        }
      }
    } catch (err) {
      vm.errorText = err;
    }
  }

  //#endregion drawing

  //#region actions

  /**
   * 
   * @param vm 
   * @param a 
   */
  async actionInvoke(vm: IViewModel, a: IActionViewModel) {
    if (a.code == 'start')
      await this.actionStart(vm, a);
    else if (a.code == 'snap')
      await this.actionSnap(vm, a);
    else if (a.code == 'next')
      await this.actionNext(vm, a);
    else if (a.code == 'clear')
      await this.actionClear(vm, a);
    else if (a.code == 'submit')
      await this.actionSubmit(vm, a);
  }

  /**
   * 
   * @param vm 
   * @param a 
   */
  private async actionSubmit(vm: IViewModel, a: IActionViewModel): Promise<void> {
    const title = a.title;

    try {
      vm.actionText = undefined;
      
      this.actionSaveImage(vm);

      a.title = 'Submitting...';
      vm.actions.forEach(a => a.enabled = false);

      const data = {
        token: this.token,

        idFront: vm.images[0].imageDataUrl,
        idBack: vm.images[1].imageDataUrl,
        cardFront: vm.images[2].imageDataUrl,
        cardBack: vm.images[3].imageDataUrl
      };

      const result = await of({}).pipe(
        delay(100),
        switchMap(() => this.apiSvc.getData('PageCommand', 'credit-card-identity', 'customer-capture-submit', data, '/api/reservations', true))
      ).toPromise();

      // error
      if (result.errorText)
        throw result.errorText;
      
      // handle results
      if (result.data) {
        vm.cameraVm = undefined;
        vm.actions = [];
        vm.helpText = undefined;
        vm.completedText = 'Thank you!  A representative will reach out to you shortly.';  
      }

    } catch (err) {
      vm.actionText = err;
      a.title = title;
      vm.actions.forEach(a => a.enabled = true);
    }
  }

  /**
   * 
   * @param vm 
   * @param a 
   */
  private async actionClear(vm: IViewModel, a: IActionViewModel): Promise<void> {
    try {
      vm.actionText = undefined;

      const el = this.canvasElement.nativeElement;

      const ctx = el.getContext('2d');
      ctx.putImageData(vm.cameraVm.imageData, 0, 0);

    } catch (err) {
      vm.actionText = err;
    }
  }

  /**
   * 
   * @param vm 
   */
  private actionSaveImage(vm: IViewModel) {
    const imageDataUrl = this.canvasElement.nativeElement.toDataURL('image/png');
    const image = { code: vm.imageNdx.toString(), imageData: vm.cameraVm.imageData, imageDataUrl: imageDataUrl };

    const ndx = vm.imageNdx-1;

    // save current image
    if (vm.images.length > ndx)
      vm.images.push(image);
    else
      vm.images[ndx] = image;
  }

  /**
   * 
   * @param vm 
   * @param a 
   */
  private async actionNext(vm: IViewModel, a: IActionViewModel): Promise<void> {
    try {
      vm.actionText = undefined;

      this.actionSaveImage(vm);

      // next
      vm.imageNdx += 1;
      vm.helpText = this.helpText(vm.imageNdx);
      await this.actionStart(vm, a);

    } catch (err) {
      vm.actionText = err;
    }
  }

  private async actionStop(vm: IViewModel): Promise<void> {
    try {

      await of({}).pipe(
        delay(100)
      )
      // end camera output
      this.videoElement.nativeElement.pause();
      this.videoElement.nativeElement.srcObject = undefined;

      if (vm.cameraVm.mediaStream) {
        vm.cameraVm.mediaStream.getTracks()[0].stop();
        vm.cameraVm.mediaStream = undefined;
      }

    } catch (err) {

    }
  }

  /**
   * 
   * @param vm 
   * @param a 
   */
  private async actionSnap(vm: IViewModel, a: IActionViewModel): Promise<void> {
    try {
      vm.actionText = undefined;

      const video = this.videoElement.nativeElement;

      const osc: any = document.createElement('canvas');
      osc.width = vm.display.width;
      osc.height = vm.display.height;

      const context = osc.getContext('2d');
      context.drawImage(video, 0, 0, vm.display.width, vm.display.height);

      vm.cameraVm.imageData = context.getImageData(0, 0, osc.width, osc.height);

      await this.actionStop(vm);

      // keep picture
      vm.cameraVm.hasPicture = true;
      await of({}).pipe(delay(100)).toPromise();

      const canvas = this.canvasElement.nativeElement;
      const context2 = canvas.getContext('2d');
      context2.putImageData(vm.cameraVm.imageData, 0, 0);

      vm.actions = [
        { code: 'clear', enabled: true, title: 'Clear' },
        { code: 'start', enabled: true, title: 'Retake' },
        vm.imageNdx < 4 ? { code: 'next', enabled: true, title: 'Next' }
          : { code: 'submit', enabled: true, title: 'Submit' }
      ];

    } catch (err) {
      vm.actionText = err;
      vm.actions.forEach(a => a.enabled = true);
    }
  }

  /**
   * 
   * @param vm 
   * @param a 
   */
  private async actionStart(vm: IViewModel, a: IActionViewModel): Promise<void> {
    const title = a.title;

    try {

      vm.actionText = undefined;
      a.title = 'Starting...';
      vm.actions.forEach(a => a.enabled = false);

      vm.cameraVm.hasPicture = false;

      // start the camera
      const stm = await of({}).pipe(
        delay(100),
        tap(() => {
          this.videoElement.nativeElement.setAttribute('autoplay', '');
          this.videoElement.nativeElement.setAttribute('muted', '');
          this.videoElement.nativeElement.setAttribute('playsinline', '');
        }),
        switchMap(() => from(this._window.navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })))
      ).toPromise();

      // start camera output
      this.videoElement.nativeElement.srcObject = stm;
      this.videoElement.nativeElement.play();

      vm.cameraVm.mediaStream = stm;

      vm.actions = [
        { code: 'snap', enabled: true, title: 'Snap ' }
      ];

    } catch (err) {
      vm.actionText = err;
      a.title = title;
      vm.actions.forEach(a => a.enabled = true);
    }
  }

  //#endregion actions

  /**
   * 
   * @param imageNdx 
   * @returns 
   */
  private helpText(imageNdx: number): string {
    return imageNdx == 1 ? 'Front of license'
      : imageNdx == 2 ? 'Back of license'
        : imageNdx == 3 ? 'Front of credit card, scribble out card number leaving last 4 digits visible'
          : imageNdx == 4 ? 'Back of credit card, scribble out card number leaving last 4 digits visible'
            : undefined;
  }

  /**
   * 
   * @param vm 
   */
  private orientationChanged(vm: IViewModel) {
    try {
      const isLandscape: boolean = screen.orientation !== undefined ? screen.orientation.type.startsWith('landscape') ? true : false
        : window.orientation !== undefined ? window.orientation == 90 || window.orientation == -90
          : false;

      vm.display = {
        isLandscape: isLandscape,
        width: isLandscape ? 520 : 320,
        height: isLandscape ? 320 : 480
      };
    } catch (err) {
      vm.errorText = err;
    }
  }

  /**
   * 
   * @returns 
   */
  private infoVm(): IInfoViewModel {
    return {
      version: this.version
    };
  }

  /**
   * 
   * @param vm 
   */
  infoClosed(vm: IViewModel) {
    this.viewModelInit(vm);
  }

  /**
 * 
 * @param vm 
 */
  async infoShow(vm: IViewModel) {
    try {
      await this.actionStop(vm);

      vm.cameraVm = undefined;
      vm.infoVm = this.infoVm();

    } catch(err) {
      vm.errorText = err;
    }
  }

  /**
   * 
   * @param imageNdx 
   * @returns 
   */
  private viewModelInit(vm: IViewModel, imageNdx: number = 1): IViewModel {
    vm.cameraVm = {};
    vm.infoVm = undefined;

    vm.imageNdx = imageNdx;
    vm.images = [];
    
    vm.helpText = this.helpText(imageNdx);
    vm.completedText = undefined;

    vm.actions = [{
      code: 'start',
      title: 'Start',
      enabled: true
    }];

    return vm;
  }

  /**
   * 
   */
  ngOnInit(): void {
    this.vm$ = of({}).pipe(

      map(ctx => {
        const vm = this.viewModelInit({});

        this.orientationChanged(vm);

        // set up listener
        if (screen.orientation)
          screen.orientation.onchange = () => this.orientationChanged(vm);
        else if (window.orientation !== undefined)
          window.addEventListener('orientationchange', () => this.orientationChanged(vm));

        return vm;
      }),

      catchError(err => {
        const vm: IViewModel = {
          errorText: err,
          display: {
            isLandscape: false,
            width: 320,
            height: 480
          }
        };
        return of(vm);
      }),


    );
  }

  private readonly version: string = '1.5';

}

/**
 * 
 */
interface IViewModel {
  errorText?: string;
  helpText?: string;
  completedText?: string;

  actions?: IActionViewModel[];
  actionText?: string;

  display?: {
    isLandscape: boolean;

    width: number;
    height: number;
  };

  imageNdx?: number;
  images?: {
    code: string;
    imageData?: any;
    imageDataUrl?: string;
  }[];

  cameraVm?: {
    hasPicture?: boolean;
    mediaStream?: MediaStream;
    imageData?: any;
  };

  infoVm?: IInfoViewModel;
}

interface IInfoViewModel {
  version: string;
}

interface IActionViewModel {
  code: string;
  title: string;
  enabled: boolean;
}